import {
  existingIdsByField,
  flagsForIdGroups,
  flagsForIds,
} from "../existingIdsByField";
import {
  collectVintedThreadIds,
  getInventoryListingId,
  getMrgSaleTransactionId,
  isVintedCanonicalThreadId,
  isVintedUuidThreadId,
} from "./vintedBatches";

/** Pure cut-point analysis for a page of synced flags (parallel to items). */
export type PageSyncAnalysis =
  | { kind: "empty" }
  | { kind: "page-complete" }
  | {
      kind: "resume-from";
      /** Inclusive start index in page array (0 = newest on newest-first pages). */
      startIndex: number;
    };

export type ResolveConversationId = (
  threadId: string
) => Promise<string | null>;

/**
 * Page P (checkpoint): scan last → first.
 * If the last entry is synced, the page is complete.
 * Otherwise resume from index 0 through the page, skipping already-synced
 * items at upload time — startIndex is the first unsynced from the start,
 * or 0 if nothing synced.
 */
export function analyzeCheckpointPageSync(
  syncedFlags: boolean[]
): PageSyncAnalysis {
  if (!syncedFlags.length) return { kind: "empty" };
  if (syncedFlags[syncedFlags.length - 1]) return { kind: "page-complete" };

  let firstUnsynced = 0;
  while (firstUnsynced < syncedFlags.length && syncedFlags[firstUnsynced]) {
    firstUnsynced++;
  }
  if (firstUnsynced >= syncedFlags.length) return { kind: "page-complete" };
  return { kind: "resume-from", startIndex: firstUnsynced };
}

/**
 * Page P+1 (and later): scan start → end for contiguous synced prefix.
 */
export function analyzeForwardPageSync(
  syncedFlags: boolean[]
): PageSyncAnalysis {
  if (!syncedFlags.length) return { kind: "empty" };
  let i = 0;
  while (i < syncedFlags.length && syncedFlags[i]) i++;
  if (i >= syncedFlags.length) return { kind: "page-complete" };
  return { kind: "resume-from", startIndex: i };
}

/**
 * Thread ids to probe for an order's existing sale. List rows often only expose
 * UUID; older Firestore sales may be keyed num11/`500*`. When resolve is
 * provided and the group has a UUID but no canonical, resolve UUID →
 * conversation.id so the existence query can hit the old key.
 */
export async function threadIdGroupsForOrders(
  orders: unknown[],
  getDetails?: (order: unknown) => unknown,
  resolveConversationId?: ResolveConversationId
): Promise<string[][]> {
  const resolveCache = new Map<string, string | null>();
  const groups: string[][] = [];

  for (const order of orders) {
    const details = getDetails?.(order);
    const ids = new Set(collectVintedThreadIds(order, details));
    const primary = getMrgSaleTransactionId(order, details);
    if (primary) ids.add(primary);

    const hasCanonical = [...ids].some(isVintedCanonicalThreadId);
    if (!hasCanonical && resolveConversationId) {
      for (const id of [...ids]) {
        if (!isVintedUuidThreadId(id)) continue;
        let canonical: string | null;
        if (resolveCache.has(id)) {
          canonical = resolveCache.get(id) ?? null;
        } else {
          try {
            canonical = await resolveConversationId(id);
          } catch {
            canonical = null;
          }
          resolveCache.set(id, canonical);
        }
        if (canonical) ids.add(canonical);
      }
    }

    groups.push([...ids]);
  }

  return groups;
}

async function saleFlagsForThreadGroups(
  idGroups: string[][],
  opts: {
    uploadIds?: string[];
    importPlatform?: string;
  }
): Promise<boolean[]> {
  const unique = [...new Set(idGroups.flat().filter(Boolean))];
  const found = await existingIdsByField({
    collection: "Sales",
    idField: "transaction_id",
    ids: unique,
    uploadIds: opts.uploadIds,
    importPlatform: opts.importPlatform,
  });
  return flagsForIdGroups(idGroups, found);
}

export async function salesSyncedFlagsForPage(
  orders: unknown[],
  uploadSalesId: string,
  uploadReturnsId: string,
  getDetails?: (order: unknown) => unknown,
  resolveConversationId?: ResolveConversationId
): Promise<boolean[]> {
  const idGroups = await threadIdGroupsForOrders(
    orders,
    getDetails,
    resolveConversationId
  );
  return saleFlagsForThreadGroups(idGroups, {
    uploadIds: [uploadSalesId, uploadReturnsId],
  });
}

/**
 * Daily sync: whether each order already has a Vinted sale booked (any upload).
 * Uses `user` + `transaction_id` with an in-memory `import_platform === "vinted"`
 * filter — no Uploads / fake upload id required.
 *
 * Alias-aware: an order whose list row is UUID still counts as existing when
 * Firestore has the sale under num11/`500*` (and the reverse when enrich/details
 * expose both). Optional `resolveConversationId` fills UUID → canonical when
 * the list row only has a UUID.
 */
export async function salesExistFlagsForPage(
  orders: unknown[],
  getDetails?: (order: unknown) => unknown,
  resolveConversationId?: ResolveConversationId
): Promise<boolean[]> {
  const idGroups = await threadIdGroupsForOrders(
    orders,
    getDetails,
    resolveConversationId
  );
  return saleFlagsForThreadGroups(idGroups, { importPlatform: "vinted" });
}

export async function inventorySyncedFlagsForPage(
  items: unknown[],
  uploadInventoryId: string
): Promise<boolean[]> {
  const ids = items.map((item) => getInventoryListingId(item));
  const unique = [...new Set(ids.filter((id): id is string => !!id))];
  const found = await existingIdsByField({
    collection: "Inventory",
    idField: "listing_id",
    ids: unique,
    uploadIds: [uploadInventoryId],
  });
  return flagsForIds(ids, found);
}
